import { isDeepEqual } from '@attain-apps/data-utils'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import { capitalize } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import { Mutex } from 'async-mutex'
import * as rxjs from 'rxjs'

import * as effects from '../../effects'
import type { Feature } from '../feature'

/**
 * Executes all actions pertaining to an individual property by using the
 * underlying write action
 */
export const relayPropertyActionsToWrite = effects.create(
  (feature: Feature<any>, key: string) => {
    /**
     * Used to ensure that there are no race conditions between parallel
     * requests
     *
     * __NOTE:__ this is only needed because each request uses the current value
     * to get the new value. This means that if multiple requests happen in
     * parallel, each request needs access to the value that was produced by the
     * previous request to ensure that no requests are lost. Consider the
     * following example:
     * ```
     * Current State: { myProp: 0 }
     * SetMyProp Action 1: currentValue => currentValue + 100
     * SetMyProp Action 2: currentValue => currentValue + 200
     * ```
     * We expect to end up with a state that looks like: `{ myProp: 300 }`.
     * However, without a mutex, the way this actually gets executed is:
     * ```
     * SetMyProp Action 1 - Get current value of myProp: 0
     * SetMyProp Action 2 - Get current value of myProp: 0
     * SetMyProp Action 1 - Calculate new value of myProp: 0 + 100 = 100
     * SetMyProp Action 2 - Calculate new value of myProp: 0 + 200 = 200
     * SetMyProp Action 1 - Write new value of myProp: 100
     * SetMyProp Action 2 - Write new value of myProp: 200
     * Final State: { myProp: 200 }
     * ```
     * With a mutex, this is corrected to:
     * ```
     * SetMyProp Action 1 - Acquire mutex
     * SetMyProp Action 1 - Get current value of myProp: 0
     * SetMyProp Action 2 - Wait for mutex
     * SetMyProp Action 1 - Calculate new value of myProp: 0 + 100 = 100
     * SetMyProp Action 1 - Write new value of myProp: 100
     * SetMyProp Action 1 - Release mutex
     * SetMyProp Action 2 - Acquire mutex
     * SetMyProp Action 2 - Get current value of myProp: 100
     * SetMyProp Action 2 - Calculate new value of myProp: 100 + 200 = 300
     * SetMyProp Action 2 - Write new value of myProp: 300
     * SetMyProp Action 2 - Release mutex
     * Final State: { myProp: 200 }
     * ```
     */
    const mutex = new Mutex()

    return rxjs.merge(
      ...([`set${capitalize(key)}`, `putIn${capitalize(key)}`] as const)
        .map(
          opName =>
            feature.actions[opName]?.relay((forwardTo, getNewValue) =>
              // Waits until we have the mutex before proceeding
              rxjs
                .defer(async () => mutex.acquire())
                .pipe(
                  // Gets the latest current value
                  rxjsUtils.latestFrom(
                    () =>
                      feature.selectors[key]
                        .asObservable satisfies rxjs.Observable<unknown> as rxjs.Observable<unknown>,
                  ),
                  // Uses that value to relay to the underlying write action
                  rxjs.map(value => {
                    // NOTE: because `getNewValue` is typed more restrictively for
                    // lookups, we need to match those more restrictive types
                    const newValue = getNewValue(
                      value as Record<string, unknown> | undefined,
                    )
                    if (isDeepEqual(value, newValue)) {
                      // If the value has not changed, we can cancel the
                      // request
                      mutex.release()
                      return {
                        cancellation: {
                          reason: 'Property already has desired value',
                        },
                      }
                    }
                    return forwardTo(feature.actions.writeProperty, {
                      input: {
                        entry: {
                          key,
                          value: newValue,
                        },
                      },
                      onResponse: () => {
                        // When the write request initially finishes (regardless
                        // of outcome), we need to release the mutex. It is
                        // important this ALWAYS happens because otherwise the
                        // mutex will be deadlocked and this effect will be
                        // unusable
                        mutex.release()
                      },
                    })
                  }),
                ),
            ).asObservable,
        )
        .filter(TypeChecker.isNotNullish.raw),
    )
  },
)
